// Carcasa de la aplicación: barra global, barra lateral del proyecto, migas, panel lateral del caso,
// diálogo de creación, paleta de comandos, atajos de teclado y avisos.
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSesion } from '../auth/contexto';
import { esPreventa } from '../lib/permisos';
import { Riel } from '../shell/Riel';
import { Avisos } from '../shell/Avisos';
import { Paleta } from '../shell/Paleta';
import { DialogoAtajos } from '../shell/Atajos';
import { ContextoUi, type AccionesUi, type Aviso } from '../shell/contexto';
import { PantallaCarga } from './Estados';
import { Icono } from './Iconos';

const PanelCaso = lazy(() => import('../pages/caso/PanelCaso'));
const CrearCaso = lazy(() => import('../pages/CrearCaso'));

const SECCIONES: Record<string, string> = {
  tablero: 'Pipeline',
  lista: 'Lista',
  solicitudes: 'Solicitudes',
  equipo: 'Equipo',
  brechas: 'Brechas',
  biblioteca: 'Biblioteca',
  'mi-trabajo': 'Mi trabajo',
  aprobaciones: 'Aprobaciones',
  excepciones: 'Excepciones',
  indicadores: 'Indicadores',
  catalogos: 'Catálogos',
  casos: 'Casos',
  bandeja: 'Bandeja de Orion',
  gestiones: 'Gestiones de preventa',
  contratacion: 'Contratación pública',
  proveedores: 'Proveedores',
};

/** Comercial es una aplicación aparte de Preventa: UPK INNOVA > Comercial > sección > detalle. */
function MigasComercial({ pathname }: { pathname: string }) {
  const partes = pathname.split('/').filter(Boolean).slice(1);
  const SUB: Record<string, string> = { oportunidades: 'Oportunidades', cuentas: 'Cuentas', prospectos: 'Prospectos', forecast: 'Forecast y metas' };
  const items: { texto: string; a?: string }[] = [{ texto: 'UPK INNOVA', a: '/comercial' }, { texto: 'Comercial', a: '/comercial' }];
  if (partes[0]) items.push({ texto: SUB[partes[0]] ?? partes[0], a: `/comercial/${partes[0]}` });
  if (partes[1]) items.push({ texto: 'Detalle' });
  if (items.length === 2) items.push({ texto: 'Inicio' });
  return <ListaMigas items={items} />;
}

/** Compras es una aplicación aparte (§14): UPK INNOVA > Compras > sección > detalle. */
function MigasCompras({ pathname }: { pathname: string }) {
  const partes = pathname.split('/').filter(Boolean).slice(1);
  const SUB: Record<string, string> = {
    bandeja: 'Bandeja',
    solicitudes: 'Solicitudes',
    proveedores: 'Proveedores',
    cotizaciones: 'Cotizaciones',
    inteligencia: 'Inteligencia de precios',
  };
  const items: { texto: string; a?: string }[] = [
    { texto: 'UPK INNOVA', a: '/compras' },
    { texto: 'Compras', a: '/compras' },
  ];
  if (partes[0]) items.push({ texto: SUB[partes[0]] ?? partes[0], a: `/compras/${partes[0]}` });
  if (partes[1]) items.push({ texto: 'Detalle' });
  if (items.length === 2) items.push({ texto: 'Tablero' });
  return <ListaMigas items={items} />;
}

/** Personas es una aplicación aparte (§15): UPK INNOVA > Personas > sección > detalle. */
function MigasPersonas({ pathname }: { pathname: string }) {
  const partes = pathname.split('/').filter(Boolean).slice(1);
  const SUB: Record<string, { texto: string; a: string }> = {
    colaboradores: { texto: 'Colaboradores', a: '/personas/colaboradores' },
    personas: { texto: 'Colaboradores', a: '/personas/colaboradores' },
    organizacion: { texto: 'Organigrama y plazas', a: '/personas/organizacion' },
    cargos: { texto: 'Cargos y perfiles', a: '/personas/cargos' },
    directorio: { texto: 'Directorio', a: '/personas/directorio' },
    mi: { texto: 'Mi Vida Laboral', a: '/personas/mi' },
    seleccion: { texto: 'Selección', a: '/personas/seleccion' },
    ingresos: { texto: 'Ingresos', a: '/personas/ingresos' },
    'textos-legales': { texto: 'Textos legales', a: '/personas/textos-legales' },
    contratos: { texto: 'Contratos', a: '/personas/contratos' },
    requisitos: { texto: 'Requisitos de ingreso', a: '/personas/requisitos' },
    usuarios: { texto: 'Usuarios y permisos', a: '/personas/usuarios' },
    bandeja: { texto: 'Bandeja de Gestión Humana', a: '/personas/bandeja' },
  };
  const items: { texto: string; a?: string }[] = [
    { texto: 'UPK INNOVA', a: '/personas' },
    { texto: 'Personas', a: '/personas' },
  ];
  if (partes[0]) items.push(SUB[partes[0]] ?? { texto: partes[0], a: `/personas/${partes[0]}` });
  if (partes[0] === 'seleccion' && partes[2]) items.push({ texto: partes[1] === 'vacantes' ? 'Vacante' : 'Candidato' });
  else if (partes[1]) items.push({ texto: partes[0] === 'personas' ? 'Expediente' : partes[0] === 'ingresos' ? 'Ingreso' : 'Detalle' });
  if (items.length === 2) items.push({ texto: 'Inicio' });
  return <ListaMigas items={items} />;
}

function ListaMigas({ items }: { items: { texto: string; a?: string }[] }) {
  return (
    <nav className="migas" aria-label="Ruta de navegación">
      <ol>
        {items.map((m, i) => (
          <li key={i}>
            {m.a && i < items.length - 1 ? <Link to={m.a}>{m.texto}</Link> : <span aria-current="page">{m.texto}</span>}
            {i < items.length - 1 && <Icono nombre="chevronDerecha" className="migas__sep" />}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function Migas() {
  const { pathname } = useLocation();
  if (pathname.startsWith('/comercial')) return <MigasComercial pathname={pathname} />;
  if (pathname.startsWith('/compras')) return <MigasCompras pathname={pathname} />;
  if (pathname.startsWith('/personas')) return <MigasPersonas pathname={pathname} />;
  const partes = pathname.split('/').filter(Boolean).slice(1);
  const seccion = partes[0] ?? 'tablero';
  const items: { texto: string; a?: string }[] = [
    { texto: 'UPK INNOVA', a: '/preventa/tablero' },
    { texto: 'Preventa Colombia', a: '/preventa/tablero' },
  ];
  if (seccion === 'casos') {
    items.push({ texto: 'Casos', a: '/preventa/lista' });
    if (partes[1]) items.push({ texto: decodeURIComponent(partes[1]) });
  } else if (seccion === 'proveedores' && partes[1]) {
    items.push({ texto: 'Proveedores', a: '/preventa/proveedores' });
    items.push({ texto: 'Detalle' });
  } else items.push({ texto: SECCIONES[seccion] ?? 'Página' });
  return <ListaMigas items={items} />;
}

const esEdicion = (el: Element | null) => !!el && (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement || (el as HTMLElement).isContentEditable);


/** Franja de la demostración estática; no existe en los demás builds. */
const BannerDemo = import.meta.env.VITE_DEMO ? lazy(() => import('../demo/BannerDemo')) : null;

export function Layout() {
  const sesion = useSesion();
  const navegar = useNavigate();
  const { pathname } = useLocation();
  const [params, setParams] = useSearchParams();
  const casoPanel = params.get('caso');
  const puedeCrear = esPreventa(sesion.roles) && !pathname.startsWith('/comercial') && !pathname.startsWith('/compras') && !pathname.startsWith('/personas');

  const [crear, setCrear] = useState(false);
  const [paleta, setPaleta] = useState(false);
  const [atajos, setAtajos] = useState(false);
  const [avisos, setAvisos] = useState<Aviso[]>([]);
  const secuencia = useRef(0);

  const avisar = useCallback((a: Omit<Aviso, 'id'>) => {
    secuencia.current += 1;
    const id = secuencia.current;
    setAvisos((l) => [...l.slice(-3), { ...a, id }]);
  }, []);
  const cerrarAviso = useCallback((id: number) => setAvisos((l) => l.filter((x) => x.id !== id)), []);

  const abrirCaso = useCallback((caseId: string) => navegar(`/preventa/casos/${encodeURIComponent(caseId)}`), [navegar]);
  const abrirVistaRapida = useCallback(
    (caseId: string) => {
      if (pathname.startsWith('/preventa/casos/')) {
        navegar(`/preventa/casos/${encodeURIComponent(caseId)}`);
        return;
      }
      const p = new URLSearchParams(params);
      p.set('caso', caseId);
      setParams(p);
    },
    [params, setParams, pathname, navegar],
  );
  const cerrarCaso = useCallback(() => {
    const p = new URLSearchParams(params);
    p.delete('caso');
    p.delete('pestana');
    setParams(p);
  }, [params, setParams]);

  const acciones = useMemo<AccionesUi>(
    () => ({
      abrirCrear: () => puedeCrear && setCrear(true),
      abrirPaleta: () => setPaleta(true),
      abrirAtajos: () => setAtajos(true),
      abrirCaso,
      abrirVistaRapida,
      cerrarCaso,
      avisar,
    }),
    [abrirCaso, abrirVistaRapida, cerrarCaso, avisar, puedeCrear],
  );

  // Atajos: c, /, Ctrl+K, g t, g l, g m, ?, Esc.
  const prefijoG = useRef(0);
  useEffect(() => {
    const alTecla = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaleta(true);
        return;
      }
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      const dialogoAbierto = !!document.querySelector('dialog[open]') || !!document.querySelector('.popover');
      if (e.key === 'Escape') {
        if (!dialogoAbierto && casoPanel && !esEdicion(document.activeElement)) {
          e.preventDefault();
          cerrarCaso();
        }
        return;
      }
      if (dialogoAbierto || esEdicion(document.activeElement)) return;
      const k = e.key.toLowerCase();
      if (Date.now() - prefijoG.current < 1200) {
        prefijoG.current = 0;
        const destino = { t: '/preventa/tablero', l: '/preventa/lista', m: '/preventa/mi-trabajo' }[k];
        if (destino) {
          e.preventDefault();
          navegar(destino);
        }
        return;
      }
      if (k === 'g') prefijoG.current = Date.now();
      else if (k === 'c' && puedeCrear) {
        e.preventDefault();
        setCrear(true);
      } else if (e.key === '/') {
        e.preventDefault();
        setPaleta(true);
      } else if (e.key === '?') {
        e.preventDefault();
        setAtajos(true);
      }
    };
    window.addEventListener('keydown', alTecla);
    return () => window.removeEventListener('keydown', alTecla);
  }, [casoPanel, cerrarCaso, navegar, puedeCrear]);

  const pantallaCompleta = pathname === '/preventa/tablero';

  return (
    <ContextoUi.Provider value={acciones}>
      <a
        href="#contenido"
        className="saltar"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById('contenido')?.focus();
        }}
      >
        Saltar al contenido
      </a>
      <div className={`app ${casoPanel ? 'app--con-panel' : ''}`}>
        <Riel />
        <main className={`contenido ${pantallaCompleta ? 'contenido--lleno' : ''}`} id="contenido" tabIndex={-1}>
          {BannerDemo && (
            <Suspense fallback={null}>
              <BannerDemo />
            </Suspense>
          )}
          <Migas />
          <Suspense fallback={<PantallaCarga />}>
            <Outlet />
          </Suspense>
        </main>
        {casoPanel && (
          <Suspense fallback={<aside className="panel-caso panel-caso--cargando" aria-busy="true" />}>
            <PanelCaso key={casoPanel} id={casoPanel} alCerrar={cerrarCaso} />
          </Suspense>
        )}
      </div>
      {crear && (
        <Suspense fallback={null}>
          <CrearCaso abierto={crear} alCerrar={() => setCrear(false)} />
        </Suspense>
      )}
      <Paleta abierta={paleta} alCerrar={() => setPaleta(false)} puedeCrear={puedeCrear} />
      <DialogoAtajos abierto={atajos} alCerrar={() => setAtajos(false)} />
      <Avisos avisos={avisos} alCerrar={cerrarAviso} />
    </ContextoUi.Provider>
  );
}
